import React, { useEffect, useRef, useMemo, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from '../../../../../core/context/ToolContext';
import { getDateRangeByFilter, shouldFetchForFilter } from '../../helpers/dateRangeHelper';
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader';
import ChartFilter from '../../helpers/chartFilter';
import { fetchFacebookInsights, fetchInstagramInsights, processInsightsData } from '../../helpers/metricHelpers';
import { generateDateRangeIntervals } from '../../helpers/dateRangeInterval';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export const FollowsChart = () => {
    const chartRef = useRef(null);
    const { social_media_insight_state } = useContext(ToolContext)
    const platform = social_media_insight_state.connectedPlatform.data;

    const listView = async () => {
        if (platform == '' || social_media_insight_state.showProfilesModal.data) return;
        try {
            social_media_insight_state.followsLoading.set(true)
            const dateRange = getDateRangeByFilter(social_media_insight_state.followFilter.data, social_media_insight_state.followCustomRange.data);
            let insightsData = [];
            if (platform == 'facebook') {
                const chartData = await fetchFacebookInsights({
                    profileId: social_media_insight_state.selectedProfileId.data,
                    accessToken: social_media_insight_state.accessToken.data,
                    metric_name: 'page_follows',
                    startDate: dateRange.start_date_unix,
                    endDate: dateRange.end_date_unix,
                });
                insightsData = chartData;
            } else if (platform == 'instagram') {
                const chartData = await fetchInstagramInsights({
                    accountId: social_media_insight_state.instagramAccountId.data,
                    accessToken: social_media_insight_state.instagramAccessToken.data,
                    metric_name: 'follower_count',
                    start_date: dateRange.start_date_unix,
                    end_date: dateRange.end_date_unix,
                });
                insightsData.data = chartData;
            }
            
            const followsByDate = processInsightsData({
                insightsData,
                startDate: dateRange.start_date,
                endDate: dateRange.end_date,
                targetMetric: platform == 'facebook' ? 'page_follows' : 'follower_count'
            });

            const followsData = generateDateRangeIntervals(
                dateRange.start_date,
                dateRange.end_date,
                followsByDate,
                platform == 'facebook' ? 'page_follows' : 'follower_count'
            );
            social_media_insight_state.followsData.set(followsData);
        } catch (error) {
            console.error('Error fetching social media insights:', error);
            social_media_insight_state.followsData.set([]);
        } finally {
            social_media_insight_state.followsLoading.set(false)
        }
    }

    useEffect(() => {
        if (shouldFetchForFilter(social_media_insight_state.followFilter.data, social_media_insight_state.followCustomRange.data)) {
            listView();
        }
    }, [social_media_insight_state.urlCode.data, social_media_insight_state.followFilter.data, social_media_insight_state.followCustomRange.data, platform]);

    const followsData = social_media_insight_state?.followsData?.data || [];
    const labels = followsData?.map(item => item.date);
    const values = followsData?.map(item => item.value);
    const yAxisConfig = calculateYAxisConfig(values);
    const baseData = followsData?.map(item => item.value);
    const additionalData = followsData?.map((item, index, arr) => {
        if (index === 0) return 0;
        return item.value - arr[index - 1].value;
    });

    const data = useMemo(() => ({
        labels,
        datasets: [
            {
                label: 'Follows',
                data: baseData,
                borderRadius: {
                    topLeft: 0,
                    topRight: 0,
                    bottomLeft: 8,
                    bottomRight: 8,
                },
                barThickness: 30,
                barPercentage: 0.4,
                categoryPercentage: 1,
            },
            {
                label: 'Growth',
                data: additionalData,
                borderRadius: {
                    topLeft: 8,
                    topRight: 8,
                    bottomLeft: 0,
                    bottomRight: 0,
                },
                barThickness: 30,
                barPercentage: 0.4,
                categoryPercentage: 1,
            },
        ],
    }), [labels, baseData, additionalData]);

    useEffect(() => {
        const chart = chartRef.current;
        if (chart) {
            const ctx = chart.ctx;
            const gradient1 = ctx.createLinearGradient(0, 0, 0, 300);
            gradient1.addColorStop(0, '#8061DB');
            gradient1.addColorStop(0.38, '#8061DB');
            gradient1.addColorStop(1, 'rgba(128, 97, 219, 0.94)');

            const gradient2 = ctx.createLinearGradient(0, 0, 0, 300);
            gradient2.addColorStop(0, 'rgba(128, 97, 219, 0.3)');
            gradient2.addColorStop(1, 'rgba(128, 97, 219, 0.1)');

            if (chart.data.datasets) {
                chart.data.datasets[0].backgroundColor = gradient1;
                chart.data.datasets[1].backgroundColor = gradient2;
                chart.update();
            }
        }
    }, [data]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display: false,
                },
                stacked: true,
                ticks: {
                    padding: 5,
                    color: '#666',
                    maxRotation: 0,
                    autoSkip: false
                }
            },
            y: {
                ...yAxisConfig,
                grid: {
                    color: '#f3f3f3',
                    drawBorder: false,
                },
                border: {
                    display: false
                },
                stacked: true
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#061D22',
                padding: 12,
                cornerRadius: 18,
                displayColors: false,
                titleFont: {
                    size: 14,
                    weight: '600'
                },
                bodyFont: {
                    size: 14,
                    weight: '600'
                },
                callbacks: {
                    title: () => '',
                    label: (context) => {
                        const label = context.dataset.label;
                        const value = context.parsed.y;
                        if (label === 'Growth') {
                            const sign = value > 0 ? '+' : value < 0 ? '-' : '';
                            return `${sign}${Math.abs(value)} ${label.toLowerCase()}`;
                        }
                        return `${value} ${label.toLowerCase()}`;
                    },
                },
                position: 'nearest',
                intersect: false,
                mode: 'index',
            },
        },
        elements: {
            bar: {
                borderSkipped: false,
            }
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box fontSize={18} fontWeight={700} mb={2}>
                    Follows
                </Box>
                <Box display="flex" gap={2}>
                    <ChartFilter
                        filterValue={social_media_insight_state.followFilter.data}
                        setFilterValue={social_media_insight_state.followFilter.set}
                        customRange={social_media_insight_state.followCustomRange.data}
                        setCustomRange={social_media_insight_state.followCustomRange.set}
                        // minDate={dayjs().subtract(2, 'year').toDate()}
                        maxDate={dayjs().toDate()}
                    />
                </Box>
            </Box>
            <div style={{ height: '300px', width: '100%' }}>
                {social_media_insight_state.followsLoading.data ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                        <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
                    </ Box>
                ) :
                    (followsData.length > 0 && followsData.find(m => m.value > 0)) ? (
                        <Bar ref={chartRef} options={options} data={data} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                            No data available
                        </Box>
                    )}
            </div>
        </Box>
    );
};
